import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import employerConsultantApi from '../../services/employerConsultantApi';
import './ConsultantRequests.css';

const ConsultantRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const navigate = useNavigate();

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await employerConsultantApi.getConsultantRequests({ status: filter });
      setRequests(response.data.requests);
    } catch (error) {
      console.error('Fetch requests error:', error);

      // Show specific error message from API if available
      const errorMessage = error?.message ||
                          error?.error?.message ||
                          error?.response?.data?.message ||
                          'Failed to fetch consultant requests';

      // Redirect employer to company profile setup if company not created
      if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('company profile')) {
        toast.error(errorMessage, { toastId: 'employer-company-required' });
        navigate('/employer/profile');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (requestId, action) => {
    let reason = '';
    if (action === 'reject' || action === 'block') {
      reason = window.prompt(`Please provide a reason for ${action}ing this consultant:`);
      if (!reason) return;
    }

    try {
      await employerConsultantApi.respondToRequest(requestId, action, reason);
      toast.success(`Request ${action}ed successfully`);
      fetchRequests();
    } catch (error) {
      console.error('Respond error:', error);
      
      // Show specific error message from API if available
      const errorMessage = error?.message || 
                          error?.error?.message || 
                          error?.response?.data?.message || 
                          `Failed to ${action} request`;
      
      toast.error(errorMessage);
    }
  };

  const statusMap = {
    pending:  { label: 'Pending',  color: 'orange', icon: 'schedule' },
    accepted: { label: 'Accepted', color: 'green',  icon: 'check_circle' },
    rejected: { label: 'Rejected', color: 'red',    icon: 'cancel' },
    blocked:  { label: 'Blocked',  color: 'red',    icon: 'block' },
  };

  const getInitials = (name) =>
    name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';

  const renderRequestCard = (request) => {
    const cfg = statusMap[request.status] || statusMap.pending;
    const c = request.consultantId;
    return (
      <div key={request._id} className="cr-card">
        <div className="cr-card-top">
          <div className="cr-avatar">
            {c?.profilePicture
              ? <img src={c.profilePicture} alt={c.name} />
              : <span>{getInitials(c?.name)}</span>
            }
          </div>
          <div className="cr-info">
            <h3>{c?.name || 'Unknown Consultant'}</h3>
            <p>{c?.email}</p>
            {c?.phone && <p>{c.phone}</p>}
          </div>
          <span className={`cr-badge cr-badge-${cfg.color}`}>
            <span className="material-symbols-outlined">{cfg.icon}</span>
            {cfg.label}
          </span>
        </div>

        <div className="cr-card-meta">
          <span>
            <span className="material-symbols-outlined">calendar_today</span>
            Requested {new Date(request.requestedAt).toLocaleDateString()}
          </span>
          {request.respondedAt && (
            <span>
              <span className="material-symbols-outlined">done_all</span>
              Responded {new Date(request.respondedAt).toLocaleDateString()}
            </span>
          )}
        </div>

        {request.rejectionReason && (
          <div className="cr-reason">
            <span className="material-symbols-outlined">error</span>
            <span><strong>Reason:</strong> {request.rejectionReason}</span>
          </div>
        )}

        {request.status === 'pending' && (
          <div className="cr-actions">
            <button className="cr-btn-success" onClick={() => handleRespond(request._id, 'accept')}>
              <span className="material-symbols-outlined">check</span>Accept
            </button>
            <button className="cr-btn-warning" onClick={() => handleRespond(request._id, 'reject')}>
              <span className="material-symbols-outlined">close</span>Reject
            </button>
            <button className="cr-btn-danger" onClick={() => handleRespond(request._id, 'block')}>
              <span className="material-symbols-outlined">block</span>Block
            </button>
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    { key: 'pending',  label: 'Pending',  dot: 'orange' },
    { key: 'accepted', label: 'Accepted', dot: 'green' },
    { key: 'rejected', label: 'Rejected', dot: 'red' },
    { key: '',         label: 'All',      dot: '' },
  ];

  return (
    <div className="emp-consultant-requests cr-container">
      <div className="cr-header">
        <div>
          <h1 className="cr-title">Consultant Requests</h1>
          <p className="cr-subtitle">Review and manage consultants requesting access to your company</p>
        </div>
      </div>

      <div className="cr-tabs">
        {tabs.map(t => (
          <button
            key={t.key}
            className={`cr-tab ${filter === t.key ? 'active' : ''}`}
            onClick={() => setFilter(t.key)}
          >
            {t.dot && <span className={`cr-tab-dot ${t.dot}`}></span>}
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="cr-loading"><div className="cr-spinner"></div><p>Loading…</p></div>
      ) : requests.length === 0 ? (
        <div className="cr-empty">
          <span className="material-symbols-outlined">inbox</span>
          <h3>No requests found</h3>
          <p>Consultant association requests will appear here</p>
        </div>
      ) : (
        <div className="cr-grid">
          {requests.map(renderRequestCard)}
        </div>
      )}
    </div>
  );
};

export default ConsultantRequests;
